import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from "react";
import { ISessionContext } from "./types";
import { useAuth0 } from "@auth0/auth0-react";
import { UserContext } from "@/contexts/UserContext/UserContext";
import variables from "@/config/variables";

const defaultContextValues: ISessionContext = {
  resetInactivityTimer: () => {},
};

export const SessionContext = createContext<ISessionContext>(defaultContextValues);

export default function SessionContextProvider({
  children,
}: {
  children: Readonly<ReactNode>;
}) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const actualTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningDialogRef = useRef<boolean>(false);
  const { logout } = useAuth0();
  const { clientType } = useContext(UserContext);
  
  // Only apply session timeout to banker routes
  const shouldApplyTimeout = clientType === "banker";
  const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
  const WARNING_TIME = 20 * 1000; // 20 seconds before timeout

  const handleSessionTimeout = useCallback(() => {
    if (warningDialogRef.current) return; // Prevent multiple dialogs
    
    warningDialogRef.current = true;
    
    // Show warning dialog
    const confirmed = window.confirm(
      "You will be signed out due to inactivity in 20 seconds. Click OK to continue your session."
    );
    
    if (confirmed) {
      warningDialogRef.current = false;
      resetInactivityTimer();
    } else {
      // Logout after warning time
      actualTimerRef.current = setTimeout(() => {
        const logoutUri = variables.bankerAuth0LogoutUri;
        logout({
          logoutParams: {
            returnTo: logoutUri,
          },
        });
      }, WARNING_TIME);
    }
  }, [logout]);

  const resetInactivityTimer = useCallback(() => {
    if (!shouldApplyTimeout) return;

    // Clear existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (actualTimerRef.current) {
      clearTimeout(actualTimerRef.current);
    }

    // Reset warning dialog flag
    warningDialogRef.current = false;

    // Set new timer
    timerRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, INACTIVITY_LIMIT - WARNING_TIME);
  }, [shouldApplyTimeout, handleSessionTimeout]);

  // Reset timer on user activity
  useEffect(() => {
    if (!shouldApplyTimeout) return;

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];
    
    const resetTimer = () => resetInactivityTimer();
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Initial timer start
    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
      
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (actualTimerRef.current) {
        clearTimeout(actualTimerRef.current);
      }
    };
  }, [shouldApplyTimeout, resetInactivityTimer]);

  return (
    <SessionContext.Provider value={{ resetInactivityTimer }}>
      {children}
    </SessionContext.Provider>
  );
} 